// src/components/coupon/CollectionSelectModal.tsx
import * as React from "react";
import useSWR from "swr";
import { Button, Modal, Space, Spin, Table, Typography } from "antd";
import AppInput from "src/components/common/AppInput";
import type { ColumnsType } from "antd/es/table";
import { getCollectionHierarchyApi } from "src/api/collection/collectionApi";

export type SelectedCollection = {
  id: number;
  name: string;
};

type Props = {
  open: boolean;
  selectedIds: number[];
  selectedItems?: SelectedCollection[];
  onClose: () => void;
  onConfirm: (selectedItems: SelectedCollection[]) => void;
};

export default function CollectionSelectModal({ open, selectedIds, selectedItems, onClose, onConfirm }: Props) {
  const [search, setSearch] = React.useState("");
  const [tempSelected, setTempSelected] = React.useState<number[]>([]);

  React.useEffect(() => {
    if (open) {
      setTempSelected([...selectedIds]);
      setSearch("");
    }
  }, [open, selectedIds]);

  const { data, isLoading } = useSWR(
    open ? ["collection/list/modal", search] : null,
    async () =>
      await getCollectionHierarchyApi({
        collectionName: search.trim() || undefined,
        page: 0,
        size: 1000,
        order: "displayOrder,asc",
      }),
    { revalidateOnFocus: false }
  );

  const allList = React.useMemo(() => {
    const content = data?.data?.content ?? [];
    const flatList: Array<{ collectionId: number; collectionName: string; collectionCode: string }> = [];

    const flatten = (nodes: any[]) => {
      nodes.forEach((node) => {
        flatList.push({
          collectionId: node.collectionId,
          collectionName: node.collectionName,
          collectionCode: node.collectionCode || "",
        });
        if (node.children && node.children.length > 0) {
          flatten(node.children);
        }
      });
    };

    flatten(content);
    return flatList;
  }, [data]);

  const filteredList = React.useMemo(() => {
    if (!search.trim()) return allList;
    const searchLower = search.toLowerCase();
    return allList.filter(
      (item) =>
        item.collectionName.toLowerCase().includes(searchLower) ||
        item.collectionCode.toLowerCase().includes(searchLower)
    );
  }, [allList, search]);

  const handleConfirm = () => {
    const result: SelectedCollection[] = tempSelected.map((id) => {
      const collection = filteredList.find((item) => item.collectionId === id);
      if (collection) return { id: collection.collectionId, name: collection.collectionName };
      const all = allList.find((item) => item.collectionId === id);
      if (all) return { id: all.collectionId, name: all.collectionName };
      const prevSelected = selectedItems?.find((item) => item.id === id);
      if (prevSelected) return prevSelected;
      return { id, name: `컬렉션 ${id}` };
    });
    onConfirm(result);
    onClose();
  };

  const columns: ColumnsType<any> = [
    { title: "ID", dataIndex: "collectionId", width: 80 },
    { title: "컬렉션명", dataIndex: "collectionName" },
    { title: "코드", dataIndex: "collectionCode", width: 160 },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="컬렉션 선택"
      width={900}
      footer={
        <Space>
          <Button onClick={onClose}>취소</Button>
          <Button type="primary" onClick={handleConfirm}>선택</Button>
        </Space>
      }
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <AppInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="컬렉션 검색" />

        <Typography.Text type="secondary">
          선택된 컬렉션: {tempSelected.length}개
        </Typography.Text>

        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
            <Spin />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredList}
            rowKey={(item) => item.collectionId}
            pagination={false}
            scroll={{ x: 1000 }}
            rowSelection={{
              selectedRowKeys: tempSelected,
              onChange: (keys) => setTempSelected(keys.map(k => Number(k))),
            }}
          />
        )}
      </Space>
    </Modal>
  );
}
